import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, FileDown, Copy, ChevronLeft, ChevronRight, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import {
  TableColumnVisibilityMenu,
  initialColumnVisibility,
  countVisibleInRange,
} from '@/components/shared/TableColumnVisibilityMenu'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cogsReportService } from '@/services/cogsReportService'
import { useAuthStore } from '@/store/auth.store'
import type { CogsReportRow } from '@/modules/inventory/reports/types'

const COGS_COLUMN_ORDER = [
  'date',
  'itemCode',
  'itemName',
  'category',
  'subCategory',
  'uom',
  'type',
  'customer',
  'invoiceNo',
  'warehouse',
  'batchCode',
  'soldQuantity',
  'unitCostPrice',
  'totalCostAmount',
] as const

function formatDateForInput(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatDisplayDate(dateString?: string) {
  if (!dateString) return '-'
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

function formatQty(n: number) {
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 })
}

function formatMoney(n: number) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function escapeCsvCell(v: string) {
  if (v.includes('"') || v.includes(',') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`
  }
  return v
}

export const CogsReport: React.FC = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)

  const [rows, setRows] = useState<CogsReportRow[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [colVis, setColVis] = useState<Record<string, boolean>>(() =>
    initialColumnVisibility([...COGS_COLUMN_ORDER]),
  )

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)

  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  useEffect(() => {
    const today = new Date()
    const start = new Date(today.getFullYear(), today.getMonth(), 1)
    setFromDate(formatDateForInput(start))
    setToDate(formatDateForInput(today))
  }, [])

  const totalPages = useMemo(
    () => (totalCount ? Math.ceil(totalCount / pageSize) : 0),
    [totalCount, pageSize],
  )

  const loadPage = useCallback(
    async (page: number, sizeOverride?: number) => {
      if (!fromDate || !toDate) {
        toast.error(t('reports.cogsSelectDates'))
        return
      }
      const size = sizeOverride ?? pageSize
      setLoading(true)
      try {
        const fromIso = `${fromDate}T00:00:00.000Z`
        const toIso = `${toDate}T23:59:59.999Z`
        const res = await cogsReportService.getCogsReport({
          fromDate: fromIso,
          toDate: toIso,
          pageNumber: page,
          pageSize: size,
        })
        setRows(res.data)
        setTotalCount(res.totalRecords ?? 0)
        setPageNumber(res.pageNumber ?? page)
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } }
        toast.error(err.response?.data?.message || t('reports.cogsLoadFailed'))
        setRows([])
        setTotalCount(0)
      } finally {
        setLoading(false)
      }
    },
    [fromDate, toDate, pageSize, t],
  )

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter((r) => {
      const blob = [
        r.date,
        r.itemCode,
        r.itemName,
        r.category,
        r.subCategory,
        r.uom,
        r.type,
        r.customer,
        r.invoiceNo,
        r.warehouse,
        r.batchCode,
        String(r.soldQuantity),
        String(r.unitCostPrice),
        String(r.totalCostAmount),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return blob.includes(q)
    })
  }, [rows, searchQuery])

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.qty += r.soldQuantity || 0
        acc.cost += r.totalCostAmount || 0
        return acc
      },
      { qty: 0, cost: 0 },
    )
  }, [filteredRows])

  const columnMenu = useMemo(
    () => [
      { id: 'date', label: t('reports.date') },
      { id: 'itemCode', label: t('reports.itemCode') },
      { id: 'itemName', label: t('reports.itemName') },
      { id: 'category', label: t('reports.category') },
      { id: 'subCategory', label: t('reports.subCategory') },
      { id: 'uom', label: t('reports.uom') },
      { id: 'type', label: t('reports.type') },
      { id: 'customer', label: t('reports.customer') },
      { id: 'invoiceNo', label: t('reports.invoiceNo') },
      { id: 'warehouse', label: t('reports.warehouse') },
      { id: 'batchCode', label: t('reports.batchCode') },
      { id: 'soldQuantity', label: t('reports.soldQuantity') },
      { id: 'unitCostPrice', label: t('reports.unitCostPrice') },
      { id: 'totalCostAmount', label: t('reports.totalCostAmount') },
    ],
    [t],
  )

  const toggleCol = (id: string) => {
    setColVis((p) => ({ ...p, [id]: !p[id] }))
  }

  const handlePageSizeChange = (v: string) => {
    const n = Number(v)
    setPageSize(n)
    setPageNumber(1)
    void loadPage(1, n)
  }

  const handleSearchClick = () => {
    void loadPage(1)
  }

  const handlePrint = () => {
    if (!selectedBranchId) {
      toast.error(t('unsellableInventory.selectBranchForWarehouses'))
      return
    }
    if (!fromDate || !toDate) {
      toast.error(t('reports.cogsSelectDates'))
      return
    }
    const params = new URLSearchParams({
      branchId: String(selectedBranchId),
      fromDate,
      toDate,
    })
    window.open(`/inventory/reports/cogs/print?${params.toString()}`, '_blank')
  }

  const handleCopy = async () => {
    if (filteredRows.length === 0) {
      toast.error(t('inventoryDispatch.nothingToCopy'))
      return
    }
    const headers = COGS_COLUMN_ORDER.filter((id) => colVis[id]).map((id) => columnMenu.find((c) => c.id === id)?.label || id)
    const lines = filteredRows.map((r) => {
      const cells: string[] = []
      if (colVis.date) cells.push(formatDisplayDate(r.date))
      if (colVis.itemCode) cells.push(r.itemCode || '')
      if (colVis.itemName) cells.push(r.itemName || '')
      if (colVis.category) cells.push(r.category || '')
      if (colVis.subCategory) cells.push(r.subCategory || '')
      if (colVis.uom) cells.push(r.uom || '')
      if (colVis.type) cells.push(r.type || '')
      if (colVis.customer) cells.push(r.customer || '')
      if (colVis.invoiceNo) cells.push(r.invoiceNo || '')
      if (colVis.warehouse) cells.push(r.warehouse || '')
      if (colVis.batchCode) cells.push(r.batchCode || '')
      if (colVis.soldQuantity) cells.push(String(r.soldQuantity ?? ''))
      if (colVis.unitCostPrice) cells.push(String(r.unitCostPrice ?? ''))
      if (colVis.totalCostAmount) cells.push(String(r.totalCostAmount ?? ''))
      return cells.join('\t')
    })
    const text = [headers.join('\t'), ...lines].join('\n')
    try {
      await navigator.clipboard.writeText(text)
      toast.success(t('inventoryDispatch.copied'))
    } catch {
      toast.error(t('reports.cogsCopyFailed'))
    }
  }

  const handleCsv = () => {
    if (filteredRows.length === 0) {
      toast.error(t('inventoryDispatch.nothingToExport'))
      return
    }
    const headers = COGS_COLUMN_ORDER.filter((id) => colVis[id]).map((id) => columnMenu.find((c) => c.id === id)?.label || id)
    const csvRows = filteredRows.map((r) => {
      const cells: string[] = []
      if (colVis.date) cells.push(escapeCsvCell(formatDisplayDate(r.date)))
      if (colVis.itemCode) cells.push(escapeCsvCell(r.itemCode || ''))
      if (colVis.itemName) cells.push(escapeCsvCell(r.itemName || ''))
      if (colVis.category) cells.push(escapeCsvCell(r.category || ''))
      if (colVis.subCategory) cells.push(escapeCsvCell(r.subCategory || ''))
      if (colVis.uom) cells.push(escapeCsvCell(r.uom || ''))
      if (colVis.type) cells.push(escapeCsvCell(r.type || ''))
      if (colVis.customer) cells.push(escapeCsvCell(r.customer || ''))
      if (colVis.invoiceNo) cells.push(escapeCsvCell(r.invoiceNo || ''))
      if (colVis.warehouse) cells.push(escapeCsvCell(r.warehouse || ''))
      if (colVis.batchCode) cells.push(escapeCsvCell(r.batchCode || ''))
      if (colVis.soldQuantity) cells.push(escapeCsvCell(String(r.soldQuantity ?? '')))
      if (colVis.unitCostPrice) cells.push(escapeCsvCell(String(r.unitCostPrice ?? '')))
      if (colVis.totalCostAmount) cells.push(escapeCsvCell(String(r.totalCostAmount ?? '')))
      return cells.join(',')
    })
    const headerLine = headers.map(escapeCsvCell).join(',')
    const blob = new Blob([[headerLine, ...csvRows].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    const url = URL.createObjectURL(blob)
    a.href = url
    a.download = `cogs-report_${fromDate}_${toDate}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success(t('inventoryDispatch.exported'))
  }

  const visibleNumericCols =
    (colVis.soldQuantity ? 1 : 0) + (colVis.unitCostPrice ? 1 : 0) + (colVis.totalCostAmount ? 1 : 0)
  const labelColSpan =
    visibleNumericCols === 0
      ? Math.max(1, countVisibleInRange(colVis, COGS_COLUMN_ORDER, 0, 14))
      : Math.max(1, countVisibleInRange(colVis, COGS_COLUMN_ORDER, 0, 11))

  return (
    <div className="cogs-report-root space-y-6 p-6">
      <style>{`
        @media print {
          .cogs-report-root .no-print { display: none !important; }
          .cogs-report-root { padding: 1rem !important; }
        }
      `}</style>

      <div className="no-print">
        <PageHeader title={t('navigation.cogsReport')} description={t('reports.cogsReportDescription')} />
      </div>

      <Card className="no-print">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.fromDate')}</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.toDate')}</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleSearchClick} disabled={loading}>
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <TableColumnVisibilityMenu columns={columnMenu} visibility={colVis} onToggle={toggleCol} />
              <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                <Copy className="h-4 w-4 mr-2" />
                {t('reports.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('reports.download')}
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                {t('reports.print')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="no-print border-b px-4 py-2 text-sm text-muted-foreground">
            {t('navigation.reports')} — {t('navigation.cogsReport')}
          </div>
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <p className="mt-4 text-muted-foreground">{t('reports.loading')}</p>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="p-12">
              <EmptyState title={t('reports.cogsNoData')} description={t('reports.cogsClickSearch')} />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      {colVis.date && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.date')}
                        </TableHead>
                      )}
                      {colVis.itemCode && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.itemCode')}
                        </TableHead>
                      )}
                      {colVis.itemName && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.itemName')}
                        </TableHead>
                      )}
                      {colVis.category && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.category')}
                        </TableHead>
                      )}
                      {colVis.subCategory && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.subCategory')}
                        </TableHead>
                      )}
                      {colVis.uom && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.uom')}
                        </TableHead>
                      )}
                      {colVis.type && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.type')}
                        </TableHead>
                      )}
                      {colVis.customer && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.customer')}
                        </TableHead>
                      )}
                      {colVis.invoiceNo && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.invoiceNo')}
                        </TableHead>
                      )}
                      {colVis.warehouse && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.warehouse')}
                        </TableHead>
                      )}
                      {colVis.batchCode && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.batchCode')}
                        </TableHead>
                      )}
                      {colVis.soldQuantity && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.soldQuantity')}
                        </TableHead>
                      )}
                      {colVis.unitCostPrice && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.unitCostPrice')}
                        </TableHead>
                      )}
                      {colVis.totalCostAmount && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.totalCostAmount')}
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRows.map((r, idx) => (
                      <TableRow key={`${r.invoiceNo}-${r.itemCode}-${r.batchCode}-${idx}`}>
                        {colVis.date && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDisplayDate(r.date)}
                          </TableCell>
                        )}
                        {colVis.itemCode && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.itemCode || '-'}</TableCell>
                        )}
                        {colVis.itemName && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.itemName || '-'}</TableCell>
                        )}
                        {colVis.category && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.category || '-'}</TableCell>
                        )}
                        {colVis.subCategory && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.subCategory || '-'}</TableCell>
                        )}
                        {colVis.uom && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.uom || '-'}</TableCell>
                        )}
                        {colVis.type && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.type || '-'}</TableCell>
                        )}
                        {colVis.customer && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.customer || '-'}</TableCell>
                        )}
                        {colVis.invoiceNo && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.invoiceNo || '-'}</TableCell>
                        )}
                        {colVis.warehouse && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.warehouse || '-'}</TableCell>
                        )}
                        {colVis.batchCode && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.batchCode || '-'}</TableCell>
                        )}
                        {colVis.soldQuantity && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(r.soldQuantity)}</TableCell>
                        )}
                        {colVis.unitCostPrice && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(r.unitCostPrice)}</TableCell>
                        )}
                        {colVis.totalCostAmount && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(r.totalCostAmount)}</TableCell>
                        )}
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2 border-primary/20 bg-primary/5 font-bold">
                      <TableCell colSpan={labelColSpan} className="px-4 py-2 text-sm text-center align-middle">
                        {t('reports.total')}
                      </TableCell>
                      {colVis.soldQuantity && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(totals.qty)}</TableCell>
                      )}
                      {colVis.unitCostPrice && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">—</TableCell>
                      )}
                      {colVis.totalCostAmount && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(totals.cost)}</TableCell>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <div className="no-print border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  {t('reports.cogsShowing', {
                    start: totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1,
                    end: totalCount === 0 ? 0 : Math.min(pageNumber * pageSize, totalCount),
                    total: totalCount,
                  })}
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void loadPage(Math.max(pageNumber - 1, 1))}
                    disabled={pageNumber <= 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void loadPage(Math.min(pageNumber + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading || totalPages === 0}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
